import styled from '@emotion/styled'
import {COLORS} from '../constants'

/**
 * Shared pill shape. Interpolated as a plain string into each pill variant below so they share one
 * size/shape definition without passing style-driving props down to the DOM.
 */
const PILL = `
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  max-width: 170px;
  padding: 2px 8px;
  border: 1px solid ${COLORS.border};
  border-radius: 999px;
  font-size: 11px;
  line-height: 1.5;
  overflow: hidden;
  white-space: nowrap;
`

/** The breadcrumb row: pills and separators wrap together on a narrow card rather than overflowing. */
export const Chain = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px 2px;
  margin-bottom: 12px;
`

/** The current node's pill — primary colour and bold, so "you are here" reads at a glance. */
export const CurrentPill = styled.span`
  ${PILL}
  border-color: ${COLORS.brand};
  background: ${COLORS.infoBg};
  color: ${COLORS.brand};
  font-weight: 700;
`

/** An upstream ancestor the viewer can jump to: a button that scrolls the tree to that node. */
export const LinkPill = styled.button`
  ${PILL}
  background: ${COLORS.cardBg};
  color: ${COLORS.foreground};
  font-family: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.brand};
    color: ${COLORS.brand};
  }
`

/** The expand affordance for a collapsed chain — same look as a link pill, reveals the hidden middle. */
export const EllipsisPill = styled.button`
  ${PILL}
  background: ${COLORS.cardBg};
  color: ${COLORS.mutedText};
  font-family: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.brand};
    color: ${COLORS.brand};
  }
`

/** An upstream ancestor the viewer cannot scroll to (e.g. an offer with no id): shown, but inert. */
export const StaticPill = styled.span`
  ${PILL}
  background: ${COLORS.cardBg};
  color: ${COLORS.mutedText};
`

/**
 * The owner name inside a pill. `min-width: 0` lets it shrink below its content width inside the
 * flex pill so a long name truncates with an ellipsis rather than overflowing (the ellipsis has to
 * live on this inline box, not the flex pill itself).
 */
export const OwnerName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** The "·" between the owner name and its level; decorative, hidden from assistive tech. */
export const Middot = styled.span`
  color: ${COLORS.mutedText};
`

/** The level suffix inside a pill ("Lvl N"), de-emphasised against the owner name. */
export const PillLevel = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  opacity: 0.75;
`

/** The "→" between two pills; decorative, hidden from assistive tech. */
export const Separator = styled.span`
  color: ${COLORS.mutedText};
  font-size: 11px;
`
